import React, { useState, useEffect, useCallback } from 'react';
import { AxiosError } from 'axios';
import {
  ITransactionUserSummary,
  IEventSummary,
  IShowRegistrationSummary,
} from '@eagerdog/interfaces';
import { Constants } from '@eagerdog/constants';

import { apiService } from 'src/services/api.service';
import { toast } from 'src/components/Toast/ToastManager';
import Button from 'src/components/Button/Button';
import Input from 'src/components/Input/Input';

import styles from './ArmbandAssignmentModal.module.scss';

interface IProps {
  transaction: ITransactionUserSummary;
  event: IEventSummary;
  onFinish(): void;
  onSkip(): void;
}

interface IArmbandRow {
  /** representative show registration — used for show_id when calling assignArmbandNumber */
  registration: IShowRegistrationSummary;
  dog_id: string;
  dog_label: string;
  show_type?: string;   // set for per-show-type scope
  arm_number: string;
  arm_number_junior: string;
}

const isPerShowType = (event: IEventSummary) =>
  event.sanctioning_club === Constants.sanctioning_club.AKC ||
  (event.sanctioning_club === Constants.sanctioning_club.UKC &&
    event.armband_generation === Constants.armband_generation.for_show);

const isAutoAssign = (event: IEventSummary) =>
  event.sanctioning_club === Constants.sanctioning_club.UKC &&
  event.auto_assign_armband_numbers !== false &&
  event.armband_order_type !== Constants.armband_order_type.breed_group_order;

const isBreedGroupOrder = (event: IEventSummary) =>
  event.armband_order_type === Constants.armband_order_type.breed_group_order;

const ArmbandAssignmentModal: React.FC<IProps> = (props) => {
  const [rows, setRows] = useState<IArmbandRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isJunior = !!(props.transaction.user as any)?.is_junior;
  const perShowType = isPerShowType(props.event);
  const autoAssign = isAutoAssign(props.event);
  const breedGroup = isBreedGroupOrder(props.event);

  const loadRegistrations = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Build a set of (show_id::dog_id) pairs from this specific transaction
      // so we only show dogs that were registered in THIS transaction, not all of
      // the user's registrations for the event.
      const transactionPairs = new Set<string>();
      for (const dsr of props.transaction.dog_show_registrations) {
        for (const dog of dsr.dogs) {
          transactionPairs.add(`${dsr.show_id}::${dog.dog_id}`);
        }
      }

      // Fetch all show registrations for the event, then filter to this transaction's entries
      const allRegs = await apiService.getEventShowRegistrations(props.event._id);

      const userId = props.transaction.user_id?.toString();
      const regs = allRegs.filter(
        (r) =>
          r.owner_id?.toString() === userId &&
          transactionPairs.has(`${r.show_id}::${r.dog_id}`)
      );

      // De-duplicate: per-show-type → one row per (dog_id, show_type), else per dog_id
      const seen = new Map<string, IArmbandRow>();

      for (const reg of regs) {
        const key = perShowType
          ? `${reg.dog_id}::${reg.show_type}`
          : reg.dog_id;

        if (!seen.has(key)) {
          seen.set(key, {
            registration: reg,
            dog_id: reg.dog_id,
            dog_label: `${reg.dog.call_name} (${reg.dog.registered_name ?? reg.dog.call_name})`,
            show_type: perShowType ? reg.show_type : undefined,
            arm_number: reg.arm_number ?? '',
            arm_number_junior: reg.arm_number_junior ?? '',
          });
        }
      }

      setRows(Array.from(seen.values()));
    } catch (e: any) {
      setLoadError(
        e.response?.data?.message ||
        'Unable to load registration records. You can assign armbands later from the Registrants tab.'
      );
    } finally {
      setLoading(false);
    }
  }, [props.event._id, props.transaction.user_id, perShowType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  const updateRow = (index: number, field: 'arm_number' | 'arm_number_junior', value: string) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    const errors: string[] = [];

    for (const row of rows) {
      try {
        if (row.arm_number.trim()) {
          await apiService.assignArmbandNumber(
            props.event._id,
            row.registration.show_id,
            row.dog_id,
            row.arm_number.trim(),
            false
          );
        }
        if (isJunior && row.arm_number_junior.trim()) {
          await apiService.assignArmbandNumber(
            props.event._id,
            row.registration.show_id,
            row.dog_id,
            row.arm_number_junior.trim(),
            true
          );
        }
      } catch (e: any) {
        const msg = e.response?.data?.message || `Error assigning armband for ${row.dog_label}`;
        errors.push(msg);
      }
    }

    setSaving(false);

    if (errors.length > 0) {
      toast.show({
        title: 'Armband Assignment',
        content: errors.join(' | '),
        duration: 10000,
        type: 'fail',
      });
    } else {
      toast.show({
        title: 'Armband Assignment',
        content: 'Armband numbers saved successfully.',
        duration: 5000,
        type: 'success',
      });
      props.onFinish();
    }
  };

  return (
    <div className={styles.ArmbandAssignmentModal}>
      <div className={styles.modalTitle}>Assign Armband Numbers</div>
      <p className={styles.modalSubtitle}>
        Assign armband numbers for each dog entered in this transaction. You can skip and assign them later from the Registrants tab.
      </p>

      {loading && (
        <p className={styles.loadingNote}>Loading registration records…</p>
      )}

      {!loading && loadError && (
        <div className={styles.errorBanner}>{loadError}</div>
      )}

      {!loading && !loadError && (
        <>
          {autoAssign && (
            <div className={styles.infoBanner}>
              Armbands are set to auto-assign for this event. Numbers have been pre-filled — you may override them below.
            </div>
          )}

          {breedGroup && (
            <div className={styles.noteBanner}>
              This event uses Breed Group Order assignment. Enter armband numbers manually.
            </div>
          )}

          <div className={styles.dogList}>
            {rows.map((row, idx) => (
              <div key={`${row.dog_id}::${row.show_type ?? 'event'}`} className={styles.dogRow}>
                <div className={styles.dogLabel}>
                  {row.dog_label}
                  {row.show_type && (
                    <span className={styles.showType}> — {row.show_type}</span>
                  )}
                </div>
                <div className={styles.inputs}>
                  <Input
                    label="Armband #"
                    type="text"
                    value={row.arm_number}
                    placeholder={breedGroup ? 'Enter armband #' : ''}
                    onChange={(e) => updateRow(idx, 'arm_number', e.target.value)}
                  />
                  {isJunior && (
                    <Input
                      label="Junior # (50–99)"
                      type="text"
                      value={row.arm_number_junior}
                      placeholder="50–99"
                      onChange={(e) => updateRow(idx, 'arm_number_junior', e.target.value)}
                    />
                  )}
                </div>
              </div>
            ))}

            {rows.length === 0 && (
              <p className={styles.emptyNote}>No registrations found for this entry.</p>
            )}
          </div>
        </>
      )}

      <div className={styles.modalActions}>
        <Button className={styles.skipBtn} onClick={props.onSkip} disabled={saving}>
          Skip
        </Button>
        {!loading && !loadError && rows.length > 0 && (
          <Button onClick={handleSave} isLoading={saving}>
            Save Armbands
          </Button>
        )}
      </div>
    </div>
  );
};

export default ArmbandAssignmentModal;
